import { useMemo } from 'react'
import { Link, useLocation } from '@truepill/tpos-react-router'
import { EditHeader, PV1Card, useEditHeaderActions } from 'components/Card'
import { usePrescribedData } from 'hooks/usePrescribedData'
import { goToViewPrescriber } from 'routes'
import type { Prescription } from 'types'
import { PV1PrescriberCardTitle } from './PV1PrescriberCardTitle'
import { PV1PrescriberEdit } from './PV1PrescriberEdit'

type PV1PrescriberCardProps = {
  prescription: Prescription
}

export const PV1PrescriberCard = ({ prescription }: PV1PrescriberCardProps): JSX.Element => {
  const { search } = useLocation()
  const editHeaderActions = useEditHeaderActions({})
  const { rxPrescriberData } = usePrescribedData({ prescription })

  // copy rxPrescriberData to PV1PrescriberCardData for mocking
  const PV1PrescriberCardData = {
    ...rxPrescriberData,
  }

  const rows = useMemo(
    () => [
      {
        label: 'Prescriber:',
        prescribed: { value: rxPrescriberData?.name },
        dispensed: {
          value: (
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={goToViewPrescriber({
                prescriberId: prescription?.prescriber?._id ?? prescription?.prescriberId,
                search,
              })}
            >
              {PV1PrescriberCardData.name}
            </Link>
          ),
        },
        testId: 'prescriber',
      },
      {
        label: 'Credential:',
        prescribed: { value: rxPrescriberData?.credential },
        dispensed: { value: PV1PrescriberCardData?.credential },
        testId: 'credential',
      },
      {
        label: 'Address:',
        prescribed: {
          value: rxPrescriberData?.address,
        },
        dispensed: {
          value: PV1PrescriberCardData?.address,
        },
        testId: 'address',
      },
      {
        label: 'Phone:',
        prescribed: { value: rxPrescriberData?.phone },
        dispensed: { value: PV1PrescriberCardData?.phone },
        testId: 'phone',
      },
      {
        label: 'NPI:',
        prescribed: { value: rxPrescriberData?.npi },
        dispensed: { value: PV1PrescriberCardData?.npi },
        testId: 'npi',
      },
      {
        label: 'DEA:',
        prescribed: { value: rxPrescriberData?.dea },
        dispensed: { value: PV1PrescriberCardData?.dea },
        testId: 'dea',
      },
    ],
    [rxPrescriberData, PV1PrescriberCardData, prescription, search],
  )

  if (!prescription) {
    return <></>
  }

  return editHeaderActions.isEditing ? (
    <PV1PrescriberEdit
      data-testid="PV1PrescriberEdit"
      onSubmit={() => {}}
      editHeaderActions={{ ...editHeaderActions, title: <PV1PrescriberCardTitle /> }}
      rxPrescriberData={rxPrescriberData}
      pv1PrescriberCardData={PV1PrescriberCardData}
    />
  ) : (
    <PV1Card
      data-testid="PV1PrescriberCard"
      title={<EditHeader title={<PV1PrescriberCardTitle />} {...editHeaderActions} />}
      rows={rows}
    />
  )
}
